/**
 * The AI arranger: trained models deciding what the rules arranger leaves to taste, kept apart
 * from it and off unless asked for.
 *
 * It never touches the source's notes. It hands the rules arranger a plan -- for each chord and
 * each player beside the tune, rest, hold or move -- that applies only to the notes the arranger
 * writes that the source never did: the notes a harmony level adds, and a doubling player's
 * notes. At `normal` the plan also gives a player the rules leave waiting a note of the chord.
 * The hands of a player of two hands are left to the rules, as the models were fitted on players.
 */

import { BOWED, GUITAR, handedIn } from "./ensembles";

/**
 * How far the AI arranger takes part. `off` is the rules arranger alone. `light` lets it say who
 * walks a chord added and who holds it. `normal` also lets it rest players, leaving out the
 * notes added or doubled where they rest, and gives the players it does not rest a note of each
 * chord where the rules leave them waiting.
 */
export const AI_LEVELS = ["off", "light", "normal"] as const;
export type AiLevel = (typeof AI_LEVELS)[number];

/** What an arrangement says of the AI arranger. */
export interface ArrangeAi {
  level: AiLevel;
  /** Why no plan was made at a level other than `off`, the rules then arranging alone. */
  unplanned: string | null;
}

/** A plan the rules arranger followed. */
export interface ArrangePlanned {
  /** Who made it, as `model:rk_busy`. */
  source: string;
  /** Notes added, and notes doubling a line, that its rests left out; in no other count. */
  rested: { added: number; doubled: number };
  /**
   * How many times it gave a player the rules left waiting a note of the chord, at `normal`;
   * the notes are among those added.
   */
  given: number;
}

/**
 * Where the arranging starts. `score` hands the players the score's own notes, as the rules
 * arranger does, the AI arranger planning only the notes it adds. `tune` keeps only the score's
 * tune and the chords under it and writes every part anew: the book's own texture at `off`, the
 * models saying who walks the harmony at `light` and who rests too at `normal`. Neither writes
 * a tune, a bass or a chord of its own. `model` keeps only the score's tune and has the note
 * model -- a model of our own, fitted on string quartets -- write the notes of every other part
 * under it, which the check then holds to the book's rules. The model writes a quartet, and
 * the players asked for are each given a place in it (`modelWrites`).
 */
export const ARRANGE_STARTS = ["score", "tune", "model"] as const;
export type ArrangeStart = (typeof ARRANGE_STARTS)[number];

/**
 * The players the note model was fitted on, in score order: those a request in words for the
 * model is given where the players it leaves are not ones the model writes for.
 */
export const MODEL_ROSTER: readonly string[] = ["violin", "violin", "viola", "cello"];

/**
 * Whether the note model writes for `roster`: a player beside the one who carries the tune, or
 * one who sounds more than its tune alone -- a player of two hands, whose two hands are both,
 * a guitar, which stops a chord under it, or a bowed string, which stops a note under it. Every player
 * takes a place in the quartet the model writes, so two violins, a viola and a cello are
 * written for as it was fitted, a player of two hands is written every part of it, each note
 * in the hand that can take it, a guitar every part of it too, taking what its left hand can stop at
 * once, and a bowed string alone takes of them one note under each note of its tune, as a
 * double stop it can play.
 */
export function modelWrites(roster: readonly string[]): boolean {
  return (
    roster.length > 1 ||
    handedIn(roster).length > 0 ||
    roster.some((one) => one === GUITAR || BOWED.includes(one))
  );
}

/**
 * The note model's add-ons: what it is told of the source beyond its tune. The model itself is
 * shown the tune alone and is never fitted again; each add-on is fitted on top of it, apart from
 * every other, and is put on or left off by name. `harmony` tells it what the source's
 * accompaniment sounds, quarter by quarter, and which note is lowest; `rhythm` when the
 * accompaniment strikes a note and how busy the bar is; `roles` how much of each bar's
 * accompaniment is each player's own -- the lowest notes the cello's, the middle the viola's, the
 * highest the second violin's -- as how often that share strikes and how long it sounds.
 * `together` is one add-on fitted on all of that at once, and on when each player's own share
 * strikes besides: what the three tell apart, learnt together, so it stands in for them rather
 * than beside them. `layers` is one add-on told all that `together` is and, besides, where each
 * player's share repeats an earlier bar, fitted with each of those now told and now withheld:
 * so any of them (`ARRANGE_LAYERS`) can be left out when it is put on. `more` is one add-on
 * told all that `together` is and, besides, how much more than its share of the score each
 * player is to write (`ARRANGE_MORE`): the one add-on whose players write more notes than the
 * score has. `line` is one add-on told all that `together` is and, besides, where each player's
 * own share goes note by note: the pitch classes it strikes here and next and the steps between
 * its notes, no octave named. Those seven are fitted. `breath`, `spacing`, `bass`, `lowest`,
 * `present`, `quarter`, `release`, `breathmark`, `marks`, `dynamics`, `over`, `octave`, `apart`, `duet`, `fifth`, `restate`, `top`, `third`, `doubled`, `room`, `tremolo`, `mute`, `bridge`, `flutter` and `harmonic` are not: each is held
 * (`ARRANGE_HELD`), set and never fitted, with no weights of its own, so it is on beside any
 * of the seven or none. With `breath` a player who sounds on breath -- a wood-wind, a brass
 * player, a voice -- is given an eighth's rest before a bar line once its line has sounded
 * sixteen quarters without one, and a bowed player is given nothing. With `spacing` no player
 * above the lowest part begins a note more than a fifth under the part above it or a sixth
 * under the tune, nor above the tune: the book's distances between the parts, which players
 * of one compass -- four cellos -- do not keep by themselves. With
 * `bass` the lowest part begins every note on the pitch class of the lowest note the source's
 * accompaniment sounds at that moment: the score's bass, which `spacing` otherwise costs.
 * With `lowest` the lowest part is kept the lowest: no part over it begins a note under the
 * lowest octave the lowest part can play of the score's bass, in that quarter or any after it
 * in the bar, and the lowest part begins a note on that bass no higher than the lowest note a
 * part over it sounds while the bass lasts, so that the bass heard is the lowest part's and
 * not one passed under it. The next three hold the lowest part too, each one thing of it,
 * reading the score's bass by the quarter: the lowest note the source's accompaniment sounds
 * in it. With `present` the lowest part sounds in every quarter where the parts over it sound
 * and the lowest note they sound is not that bass: where the model would leave it silent it
 * begins a note. With `quarter` the lowest note the lowest part sounds in a quarter is that
 * bass: it begins no note under one of its own that is on it, and none off it without one,
 * which `bass`, reading the moment a note begins, lets a broken chord do. With `release` a
 * note of the lowest part is given no length that carries it into a quarter whose bass is
 * another note: it ends where the score's bass moves.
 * `breathmark`, `marks` and `dynamics` are marked on the page and not kept by what is written.
 * `breathmark` is the tune's own breath: the tune's notes are the source's and are never
 * changed, so where a wood-wind, a brass player or a voice plays the tune, the note it ends a
 * bar with is marked a breath once the tune has sounded sixteen quarters with neither a rest of
 * an eighth nor a mark. `marks` is the source's own marks on the parts the model writes, which
 * are bare without it: a note a player begins where its share of the source strikes is accented
 * or held as the source's notes there are, cut short as they are where it is no longer than
 * they, and slurred to its next where they are slurred on and that next is another pitch; and a
 * bowed player plucks through a bar its share plucks from end to end, the tune's player the
 * notes of the tune the source plucks, through each bar where it can pluck every one of them.
 * `dynamics` is the source's own dynamics on those parts, which without it are played at the
 * tune's: a note a player begins has the level in force in its share of the source's
 * accompaniment -- the level of the last note that share struck, the loudest of them where it
 * plays more than one share -- and, where that share has struck nothing since the bar before,
 * the level the accompaniment last struck at; a note of the tune's whose own part states no
 * level has the accompaniment's. The three are written in the arrangement's MusicXML, and the
 * model writes every note with them as without. `over` is a pianist's hands over a low tune.
 * The model reads every tune as a first violin's, and under a tune it reads above where it
 * sounds -- a cello's -- the players are seated as far under where it writes them; a pianist
 * so seated plays mostly below middle C, where a hand takes nothing close, and loses notes
 * and the score's bass with them. With `over` such a pianist plays where the model writes it
 * and is spaced by the tune as the model reads it, the tune's player sounding under its right
 * hand; a pianist alone, a pianist under a tune read where it sounds and every other player
 * are written as without it, and a harpist, an organist and a celesta's player are held as a pianist is. `octave` is the bass's octave: the book leaves the bass up to an
 * octave under the part above it, and a lowest player whose compass ends low -- a bass
 * singer, a bassoon -- cannot follow parts held up under a high tune. With `octave` the part
 * next over the lowest begins no note more than an octave above the highest octave that
 * player's compass has of the score's bass in that quarter, and each part over that one a
 * fifth more; a lowest player who reaches as high as the parts over it, a cello, is seldom
 * held. `apart` is the wood-wind kept apart: the book has the wood-wind of a chord doubled or
 * not throughout a passage, and the model, writing each a line of its own, has one on
 * another's note in one chord and off it in the next. With `apart` a wood-wind player would
 * rather begin a note off the pitch another wood-wind is sounding, the tune's player among
 * them; it is a choice among what every other hold leaves the part, and where it would leave
 * the part no note of the score's harmony the unison stands. A roster with fewer than two
 * wood-wind players is written as without it. `duet` is two voices alone: the book's rule for
 * two voices has no empty fourth or fifth, eleventh or twelfth between them on a bar's first
 * beat, and the model, resting each voice where it will, brings four voices to such a beat two
 * at a time. With `duet` the voice written last -- the lowest that sings -- sings on a bar's
 * first beat where two other voices alone would stand such an interval apart on it, and beside
 * one other voice would rather begin no note there that makes one, among what every other hold
 * leaves it. A voice resting a whole bar for a breath is not made to sing, and a roster with
 * fewer than two voices is written as without it. `fifth` is the fifth chosen: `spacing` lets a
 * part go where nothing of the score's sounds under its share of it, and where its compass ends
 * under the fifth -- a horn under a clarinet a third over its highest note. With `fifth` a part
 * would rather begin no note more than a fifth under the part above it at such a moment, nor
 * more than a fifth above the highest note of the part under it, among what every other hold
 * leaves it and only where one of the notes so chosen is of the score's harmony there.
 * `restate` is the level restated: a part is marked a dynamic where one note of its begins at
 * another level than the last, so a part holding a note over a bar line the score changes level
 * at is left at the old one, beside parts that strike there at the new. With `restate` the
 * notehead such a note is carried onto in the new bar is marked the level a note begun there
 * would have, where that is a level and not an accent and not what the part was last told. No
 * note is changed; the hands of a player of two hands and a player alone are not restated. `top` is the tune on
 * top: the lowest part, held to the pitch class of the score's bass and to no octave of it on the
 * upper notes of a broken chord, is now and then written above the tune, and the rules take the
 * top note for the melody. With `top` it begins no note above the lowest the tune is sounding,
 * nor, where the tune is silent, above the highest the parts over it are sounding; a player alone
 * under the tune is not held. `third` is a third voice: `duet` holds the voice written last, and
 * a bass singer on the score's bass has one pitch class to choose from, so the bass and one voice
 * still stand alone an empty interval apart on a bar's first beat. With `third` the voice next
 * over the lowest sings there, where one voice over it sounds an empty fourth or fifth from the
 * note the lowest voice holds or from the score's bass it would begin on. `doubled` is the tune
 * or the bass doubled: `spacing` and `fifth` still leave a part more than a fifth under the next
 * where the score sounds no note for it and where the tune is too high for it. With `doubled` a
 * part that may begin a note more than a fifth under the part above it would rather begin one
 * within the fifth that is of the pitch class of the tune's note at that moment or of the
 * score's bass in that quarter, and the part next under the tune the octave under it; where no
 * such note is left it the part is written as without. `room` is room for a wood-wind bass: the
 * lowest part is written last, and on the upper notes of a broken chord it is left one pitch,
 * which a wood-wind written before it may have taken. With `room`, where the lowest note the
 * score sounds is off its bass of the quarter, a wood-wind over a wood-wind on the lowest part
 * would rather begin a note of the score's harmony that is not of that note's pitch class;
 * where no such note is left it the part is written as without. `tremolo` is the score's
 * tremolo carried: a bowed player is marked one, as fast as the score's, on a note it begins
 * where the score's own bowed strings shake every note they strike for it at that moment
 * (where none of them strikes one, where every note the score strikes for it is shaken), and
 * a bowed player carrying the tune where the score shakes it. A note `marks` has plucked is not shaken, the
 * blown, a player of two hands and a guitar are told nothing, and no note is changed. `mute`
 * is the score's mutes carried: a bowed player is told *con sord.* through a bar where every
 * note the score's own bowed strings strike in it is muted, a brass player where every note its
 * brass strikes is, through a bar they rest in as in the bar before, and *senza sord.* where
 * that ends; it is asked of the kind and not of the player's share, since a mute takes a rest
 * to put on. `bridge` is its *sul ponticello*: a bowed player is told *sul pont.* through a bar
 * where every note the score's bowed strings strike for it in it is bowed at the bridge, and
 * *ord.* where that ends, never on a note `marks` has plucked. `flutter` is the strokes on its
 * winds: a wood-wind or a brass player is marked them, three the flutter-tongue and fewer the
 * note tongued again, on a note it begins where the score's own winds shake every note they
 * strike for it, and a wind carrying the tune where the score shakes it. `harmonic` is its
 * natural harmonics: a bowed player is marked the circle on a note it begins where the score's
 * own bowed strings play every note they strike for it as a harmonic and the note is one of the
 * four an open string of its instrument has, and a bowed player carrying the tune where the
 * score has one its instrument has; never on a note `marks` has plucked. None of the four
 * changes a note.
 */
export const ARRANGE_HINTS = [
  "harmony",
  "rhythm",
  "roles",
  "together",
  "layers",
  "more",
  "line",
  "breath",
  "spacing",
  "bass",
  "lowest",
  "present",
  "quarter",
  "release",
  "breathmark",
  "marks",
  "dynamics",
  "over",
  "octave",
  "apart",
  "duet",
  "fifth",
  "restate",
  "top",
  "third",
  "doubled",
  "room",
  "tremolo",
  "mute",
  "bridge",
  "flutter",
  "harmonic",
] as const;
export type ArrangeHint = (typeof ARRANGE_HINTS)[number];

/**
 * The add-ons that are held, not fitted: each a limit the part's grammar keeps while the model
 * writes -- or a mark on the page -- set and never fitted. One is on beside any
 * fitted add-on or none, and the strength, which is how far the fitted ones are put on, does
 * not reach it.
 */
export const ARRANGE_HELD: readonly ArrangeHint[] = [
  "breath",
  "spacing",
  "bass",
  "lowest",
  "present",
  "quarter",
  "release",
  "breathmark",
  "marks",
  "dynamics",
  "over",
  "octave",
  "apart",
  "duet",
  "fifth",
  "restate",
  "top",
  "third",
  "doubled",
  "room",
  "tremolo",
  "mute",
  "bridge",
  "flutter",
  "harmonic",
];

/**
 * How much more than the score the `more` add-on is asked for, least first: each player strikes
 * at up to half as many moments again as its share of the score's bar does, up to twice as
 * many, up to three times, or more than that. A player the score gives nothing in a bar is told
 * to rest whatever is asked.
 */
export const ARRANGE_MORE = [1, 2, 3, 4] as const;
export type ArrangeMore = (typeof ARRANGE_MORE)[number];
/** The amount asked where none is named: up to twice the score's. */
export const MORE_USUAL: ArrangeMore = 2;

/**
 * What the `layers` add-on can be told of the score's accompaniment, each put on or left out by
 * itself: `harmony` what it sounds and which note is lowest; `rhythm` when it strikes; `roles`
 * how much of each bar is each player's own; `part` when that player's own share strikes; and
 * `again` where that share repeats what it played one, two or four bars before -- its moments,
 * its shape, or its very notes. A layer left out is one the model decides for itself.
 */
export const ARRANGE_LAYERS = ["harmony", "rhythm", "roles", "part", "again"] as const;
export type ArrangeLayer = (typeof ARRANGE_LAYERS)[number];

/**
 * What the note model's parts kept of the score, and of themselves, counted of the parts as
 * checked. The score's accompaniment is every note of it that is not the tune. A part's shape in
 * a bar is the steps between the notes it strikes there.
 */
export interface ArrangeFollowed {
  /** Quarters of the passage where both the parts and the score's accompaniment sound. */
  quarters: number;
  /** Pitch classes the parts sound in those quarters, and those of them the score sounds. */
  sounded: number;
  shared: number;
  /** Those quarters where the parts' lowest note is the score's lowest, as a pitch class. */
  bass: number;
  /** Moments the parts strike at, those of them the score strikes at, and the score's own. */
  struck: number;
  met: number;
  moments: number;
  /** Bars where a part has a shape and played in the bar before, and those repeating it. */
  after: number;
  repeated: number;
  /** Bars where a part's shape is three notes or more, and those it has played before. */
  shaped: number;
  recalled: number;
}

/** What an arrangement started from the tune says of the AI arranger. */
export interface TunedAi {
  level: AiLevel;
  /** Who decided the texture, as `model:rk_busy`; `null` for the book's own. */
  source: string | null;
  /** Seat-chords the models rested and set walking, out of `cells`: every seat beside the
   *  tune, at every chord. All 0 at `off`. */
  rested: number;
  moving: number;
  cells: number;
}
